import React, { useEffect, useMemo, useRef } from 'react';

interface MapContainerProps {
  /** 百度地图 SDK 挂载点的 ref，由 useMapBootstrap 消费 */
  mapSdkMountRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 地图外层视口容器 ref，用于动态调整宽度以适配右侧抽屉 */
  mapViewportRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 当前编辑器模式，用于绘制模式下的光标样式 */
  sessionMode: string;
  /** 路径规划选点阶段（pickingStart/pickingEnd），用于选点光标样式 */
  isRoutePicking?: boolean;
  /** 是否显示编辑表单，控制左侧遮罩 */
  showEditForm: boolean;
  /** 抽屉是否打开，控制外层容器宽度 */
  isDrawerOpen: boolean;
  /** 地图尺寸变化时调用（抽屉动画结束后触发） */
  onResize: () => void;
  /** 渲染在地图容器内的子元素（SearchTools、Legend 等） */
  children: React.ReactNode;
}

/**
 * 地图容器组件
 *
 * 职责：
 * 1. 提供百度地图 SDK 的挂载 DOM（baidu-map-container）
 * 2. 控制外层容器宽度（75% / 100%）以适配右侧抽屉
 * 3. 渲染地图辅助元素：中心定位图标、比例尺、编辑遮罩
 * 4. 抽屉宽度变化完成后触发 onResize，通知地图重新计算尺寸
 */
const MapContainerComponent: React.FC<MapContainerProps> = ({
  mapSdkMountRef,
  mapViewportRef,
  sessionMode,
  isRoutePicking,
  showEditForm,
  isDrawerOpen,
  onResize,
  children,
}) => {
  const resizeTimerRef = useRef<number | null>(null);

  /** 外层容器宽度样式：用 useMemo 稳定引用，避免不必要的重渲染 */
  const containerStyle = useMemo(
    () => ({ width: isDrawerOpen ? '75%' : '100%' }),
    [isDrawerOpen]
  );

  /** 绘制模式 / 路径规划选点阶段追加光标样式 */
  const mapClassName = useMemo(
    () =>
      `baidu-map-container ${
        sessionMode === 'createDrawing' ? 'drawing-cursor' : ''
      } ${isRoutePicking ? 'route-picking-cursor' : ''}`,
    [sessionMode, isRoutePicking]
  );

  useEffect(() => {
    if (mapViewportRef.current) {
      mapViewportRef.current.style.width = isDrawerOpen ? '75%' : '100%';
    }
    if (mapSdkMountRef.current) {
      mapSdkMountRef.current.style.width = '100%';
      mapSdkMountRef.current.style.height = '100%';
    }

    resizeTimerRef.current = window.setTimeout(() => {
      onResize();
    }, 300);

    return () => {
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
      }
    };
  }, [isDrawerOpen, showEditForm, sessionMode, mapViewportRef, mapSdkMountRef, onResize]);

  return (
    // 地图外层容器：根据抽屉开合动态占据 75% 或 100% 宽度
    <div
      className="map-container"
      ref={mapViewportRef}
      style={containerStyle}
    >
      {/* 百度地图 SDK 挂载点：地图画布由 BMapGL 在该 div 内部初始化并渲染 */}
      <div ref={mapSdkMountRef} className={mapClassName} />

      {/* 地图中心定位图标：固定展示在可视区域中心，用于辅助定位 */}
      <img
        src={require('@/assets/image/location.png')}
        className="map-center-icon"
        alt="Map Center"
      />

      {/* 编辑表单遮罩：表单打开时覆盖地图左侧，阻止地图交互并提示用户当前在编辑态 */}
      <div className={showEditForm ? 'showLeftMask' : 'clearMask'} />

      {/* 比例尺控件占位：样式由 CSS 提供，用于展示地图比例尺 */}
      <div className="map-editor-scale-line" />

      {/* 地图容器内其他子元素：如 SearchTools、Legend 等浮层组件 */}
      {children}
    </div>
  );
};

export const MapContainer = React.memo(MapContainerComponent);
